import type { ChatType } from '@kanbu/schema';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';
import { useMemo, useState } from 'react';

import { TablePage } from '@/components/page/TablePage';
import { MineOnlyToggle } from '@/components/partner/MineOnlyToggle';
import { useChatsCols } from '@/features/chats/useChatsCols';
import { useDetailRowHandler, useTableQuery } from '@/hooks';
import { partnerOrSuperAdminGuard } from '@/lib/routerUtils';
import { trpc, trpcClient } from '@/services/trpc';

export const Route = createFileRoute('/_baseLayout/chats/')({
  beforeLoad: async () => partnerOrSuperAdminGuard(),
  component: ChatsPage,
});

export function ChatsPage() {
  const { t } = useLingui();
  const columns = useChatsCols();
  const [mineOnly, setMineOnly] = useState(false);

  const { data: partnerContext } = trpc.partnerLinks.myContext.useQuery();

  const mineWhere = useMemo(
    () =>
      mineOnly
        ? {
            organization: {
              id: { $in: partnerContext?.myClientOrganizationIds ?? [] },
            },
          }
        : undefined,
    [mineOnly, partnerContext?.myClientOrganizationIds],
  );

  const handleQuery = useTableQuery(trpcClient.chats.findAllAccessible.query, {
    populate: ['documentsCount', 'embeddingsCount', 'organization'],
    where: mineWhere,
  });

  const handleRow = useDetailRowHandler<ChatType>(row => ({
    to: '/chats/$chatId/edit',
    params: {
      chatId: row.original.id,
    },
  }));

  return (
    <TablePage
      title={t`Chats`}
      breadcrumbs={[{ label: t`Chats`, to: '/chats' }]}
      createButton={{
        to: '/chats/create',
        label: t`Create chat`,
      }}
      actions={
        partnerContext?.isPartnerCapable ? (
          <MineOnlyToggle value={mineOnly} onChange={setMineOnly} />
        ) : undefined
      }
      columns={columns}
      queryKey={[...getQueryKey(trpc.chats.findAllAccessible), { mineOnly }]}
      onQueryFn={handleQuery}
      onRow={handleRow}
    />
  );
}
